/**
 * Subscription Checkout API Client
 *
 * API functions for subscription booking via Stripe Checkout.
 * Follows the same pattern as TopUpSection (Stripe Checkout flow).
 */

import { apiClient } from '@/lib/api-client';

// ===========================================
// TYPES
// ===========================================

export interface SubscriptionPreview {
  new_price_id: string;
  is_upgrade: boolean;
  estimated_charge: number;
  period_end: string;
}

export interface CreateCheckoutSessionRequest {
  tier_code: string; // Changed: Backend creates Price on-the-fly
  billing_cycle: 'monthly' | 'annual';
  pricing_model?: 'credit' | 'dimension';
  credits?: number; // Credits per month (all tiers)
  products?: number; // For dimension-based (Pro/Enterprise)
  channels?: number; // For dimension-based (Pro/Enterprise)
  test_clock_id?: string; // Stripe Test Clock (staging/dev only)
}

export interface CreateCheckoutSessionResponse {
  success: boolean;
  checkout_url?: string;
  session_id?: string;
  error?: string;
  change_type?: 'upgrade' | 'downgrade';
  message?: string;
  new_amount?: number;
  billing_cycle?: string;
}

export interface UpgradePreviewResponse {
  success: boolean;
  preview?: {
    proration_amount: number;
    total: number;
    subtotal: number;
    currency: string;
    current_period_end: number;
    new_amount: number;
    new_billing_cycle: string;
    old_tier: string;
    new_tier: string;
    reason: string;
  };
  error?: string;
}

export interface ScheduledDowngrade {
  to_tier_code: string;
  to_price_eur?: number;
  effective_date: number; // Unix timestamp
  schedule_id: string;
}

export interface Subscription {
  id: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'unpaid';
  tier_code: string;
  tier_name: string;
  monthly_price_eur: number;
  product_limit: number | null;
  storage_limit_gb: number | null;
  channel_count: number | null;
  subscription_credits: number;
  purchased_credits: number;
  available_credits: number;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  stripe_subscription_id: string | null;
  stripe_customer_id: string | null;
  scheduled_downgrade: ScheduledDowngrade | null;
}

// ===========================================
// API FUNCTIONS
// ===========================================

/**
 * Create Stripe Checkout session with lazy Price creation
 *
 * Backend calculates price and creates Stripe Price on-the-fly.
 * No need to manage Price IDs manually - dimensions are sent instead.
 *
 * @param request - Checkout request with tier_code and pricing config
 * @returns Checkout URL to redirect to
 */
export async function createCheckoutSession(
  request: CreateCheckoutSessionRequest
): Promise<CreateCheckoutSessionResponse> {
  try {
    const response = await apiClient.post<CreateCheckoutSessionResponse>(
      '/api/v2/subscription/checkout/create',
      request
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Subscription Checkout Error:', error);

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: CreateCheckoutSessionResponse } };
      if (axiosError.response?.data) {
        return axiosError.response.data;
      }
    }

    return {
      success: false,
      error: 'Fehler bei der Checkout-Erstellung',
    };
  }
}

/**
 * Get active subscription for current workspace
 *
 * Returns null if no active subscription exists.
 */
export async function getActiveSubscription(): Promise<Subscription | null> {
  try {
    const response = await apiClient.get<{ subscription: Subscription | null }>(
      '/api/v2/subscription/active'
    );
    return response.data.subscription;
  } catch (error: unknown) {
    console.error('Get Active Subscription Error:', error);

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null; // No active subscription
      }
    }

    throw error;
  }
}

/**
 * Preview subscription change (upgrade/downgrade)
 *
 * Shows estimated charge and proration details.
 *
 * @param images - Desired images per month
 * @param products - Desired product limit
 * @returns Preview with pricing details
 */
export async function previewSubscriptionChange(
  images: number,
  products: number
): Promise<SubscriptionPreview> {
  try {
    const response = await apiClient.post<SubscriptionPreview>(
      '/api/v2/subscription/preview-change',
      { images, products }
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Preview Subscription Change Error:', error);
    throw error;
  }
}

/**
 * Preview upgrade proration (no payment)
 *
 * Shows exact proration amount BEFORE user confirms upgrade.
 * EU-compliant: User sees cost before payment.
 *
 * @param request - Upgrade request with tier_code and pricing config
 * @returns Proration preview details
 */
export async function previewUpgrade(
  request: CreateCheckoutSessionRequest
): Promise<UpgradePreviewResponse> {
  try {
    const response = await apiClient.post<UpgradePreviewResponse>(
      '/api/v2/subscription/preview-upgrade',
      request
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Preview Upgrade Error:', error);

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: UpgradePreviewResponse } };
      if (axiosError.response?.data) {
        return axiosError.response.data;
      }
    }

    return {
      success: false,
      error: 'Fehler bei der Proration-Vorschau',
    };
  }
}

/**
 * Confirm and execute upgrade (after preview)
 *
 * ONLY call this after user has seen and confirmed the proration via previewUpgrade!
 * Executes the upgrade and charges the proration amount immediately.
 *
 * @param request - Upgrade request (same as preview)
 * @returns Upgrade result
 */
export async function confirmUpgrade(
  request: CreateCheckoutSessionRequest
): Promise<CreateCheckoutSessionResponse> {
  try {
    const response = await apiClient.post<CreateCheckoutSessionResponse>(
      '/api/v2/subscription/confirm-upgrade',
      request
    );
    return response.data;
  } catch (error: unknown) {
    console.error('Confirm Upgrade Error:', error);

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { data?: CreateCheckoutSessionResponse } };
      if (axiosError.response?.data) {
        return axiosError.response.data;
      }
    }

    return {
      success: false,
      error: 'Fehler beim Upgrade',
    };
  }
}

/**
 * Get Stripe Customer Portal URL
 *
 * Returns URL to Stripe's hosted billing portal where users can:
 * - Update payment method
 * - View invoices
 * - Cancel subscription
 */
export async function getCustomerPortalUrl(): Promise<string> {
  try {
    const response = await apiClient.post<{ url: string }>(
      '/api/v2/subscription/customer-portal'
    );
    return response.data.url;
  } catch (error: unknown) {
    console.error('Get Customer Portal URL Error:', error);
    throw error;
  }
}
